import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

interface UseIdleTimerOptions {
    enabled: boolean;
    idleTimeoutMs: number;
    warningMs: number;
    onTimeout: () => void;
}

interface UseIdleTimerResult {
    isWarning: boolean;
    secondsRemaining: number;
    stayActive: () => void;
}

/**
 * Fires onTimeout after `idleTimeoutMs` of no mouse/keyboard/scroll/touch activity.
 * `warningMs` before the deadline, isWarning flips true with a countdown — once that
 * happens, ambient page activity (a stray mouse move) deliberately does NOT reset the
 * timer; only an explicit stayActive() call (bound to the warning dialog's button)
 * does, so a genuinely-away user isn't kept signed in by a twitching cursor.
 */
export function useIdleTimer({
    enabled,
    idleTimeoutMs,
    warningMs,
    onTimeout,
}: UseIdleTimerOptions): UseIdleTimerResult {
    const [isWarning, setIsWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    const isWarningRef = useRef(false);
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;

    const clearAllTimers = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        warningTimerRef.current = null;
        timeoutTimerRef.current = null;
        countdownIntervalRef.current = null;
    }, []);

    const resetTimer = useCallback(() => {
        clearAllTimers();
        isWarningRef.current = false;
        setIsWarning(false);

        warningTimerRef.current = setTimeout(() => {
            isWarningRef.current = true;
            setIsWarning(true);
            setSecondsRemaining(Math.round(warningMs / 1000));
            countdownIntervalRef.current = setInterval(() => {
                setSecondsRemaining(s => Math.max(0, s - 1));
            }, 1000);
        }, Math.max(0, idleTimeoutMs - warningMs));

        timeoutTimerRef.current = setTimeout(() => {
            clearAllTimers();
            isWarningRef.current = false;
            setIsWarning(false);
            onTimeoutRef.current();
        }, idleTimeoutMs);
    }, [clearAllTimers, idleTimeoutMs, warningMs]);

    useEffect(() => {
        if (!enabled) {
            clearAllTimers();
            isWarningRef.current = false;
            setIsWarning(false);
            return;
        }

        resetTimer();

        const handleActivity = () => {
            if (isWarningRef.current) return; // warning is up — require an explicit "stay signed in"
            resetTimer();
        };

        ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
        return () => {
            ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
            clearAllTimers();
        };
    }, [enabled, resetTimer, clearAllTimers]);

    const stayActive = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    return { isWarning, secondsRemaining, stayActive };
}
